import React, { useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { BodyText, Icon, IconName, theme, usePressableState } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────

export const BagPaymentCta = (props: Props) => {
  const { message, a11yLabel, icon, isLoading, onPress } = props;

  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const ctaStyles = [styles.cta, isInteracting ? styles.ctaInteracting : null];
  const textStyles = [styles.text, isLoading ? styles.loading : undefined];

  return (
    <Pressable
      ref={ref}
      style={ctaStyles}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel ?? message}
      accessibilityState={{ disabled: Boolean(isLoading), busy: Boolean(isLoading) }}
      disabled={isLoading}
      onPress={onPress}
    >
      <View style={styles.icon}>
        <Icon
          name={icon}
          color={isLoading ? theme.colors.GRAY : undefined}
          width={ICON_SIZE}
          height={ICON_SIZE}
        />
      </View>

      <BodyText style={textStyles} sizeMatch={['20']}>
        {message}
      </BodyText>
    </Pressable>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const ICON_SIZE = 24;
const ICON_WRAPPER_SIZE = 32;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingLeft: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
    borderRadius: theme.radius['3xLarge'],
  },
  ctaInteracting: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius['3xLarge'],
  },
  text: {
    flex: 1,
  },
  loading: {
    color: theme.colors.GRAY,
  },
  icon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON_WRAPPER_SIZE,
    height: ICON_WRAPPER_SIZE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  message: string;
  a11yLabel?: string;
  icon: IconName;
  isLoading?: boolean;
  onPress: () => void;
};
